import { autoAvatarOf, autoColorOf, type AutoTemplate } from '@dispositio/core';

// ---------------------------------------------------------------------------
// Gezeichnete Bilder für Elemente und Szenen ohne eigenes Bild: einfache flache Formen vor einem Farbverlauf in der Farbe des Farbplatzes (core
// `autoColorOf`), wie die Porträts im Demo-Roman „Schachnovelle“. Szenen in der Farbe ihres Kapitels. Lage und weitere Farben ergeben
// sich aus dem Farbplatz – dasselbe Element sieht also immer gleich aus. Karten zeigen das Bild oben bündig auf 4:3 (`.ec-image`, sichtbar
// y 0–192 von 256): das Wesentliche steht dort, darunter läuft der Hintergrund weiter.
// ---------------------------------------------------------------------------

const SIZE = 256;

type Ctx = CanvasRenderingContext2D;

/** Pseudozufall aus dem Farbplatz (mulberry32), je Vorlage eine eigene Folge */
function randomOf(slot: number, salt: number): () => number {
  let a = (Math.imul(slot + 1, 0x9e3779b1) ^ Math.imul(salt + 1, 0x85ebca6b)) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Farbe zu Schwarz (t > 0) bzw. Weiß (t < 0) hin verschoben */
function shade(color: string, t: number): string {
  const ch = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
  const out = ch.map((c) => Math.round(t >= 0 ? c * (1 - t) : c + (255 - c) * -t));
  return `#${out.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

const between = (rnd: () => number, lo: number, hi: number): number => lo + rnd() * (hi - lo);

function background(g: Ctx, top: string, bottom: string): void {
  const bg = g.createLinearGradient(0, 0, 0, SIZE);
  bg.addColorStop(0, top);
  bg.addColorStop(1, bottom);
  g.fillStyle = bg;
  g.fillRect(0, 0, SIZE, SIZE);
}

function poly(g: Ctx, color: string, points: [number, number][]): void {
  g.fillStyle = color;
  g.beginPath();
  for (const [i, [x, y]] of points.entries()) {
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fill();
}

function rect(g: Ctx, color: string, x: number, y: number, w: number, h: number): void {
  g.fillStyle = color;
  g.fillRect(x, y, w, h);
}

function ellipse(g: Ctx, color: string, x: number, y: number, rx: number, ry: number): void {
  g.fillStyle = color;
  g.beginPath();
  g.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  g.fill();
}

/** Kopf und Schultern (die Form der Porträts, skaliert um `k`, Mitte der Schultern bei `x`, `base`) */
function figure(g: Ctx, slot: number, x: number, k: number, base = 262): void {
  const { skin, cloth } = autoAvatarOf(slot);
  ellipse(g, cloth, x, base, 104 * k, 100 * k);
  ellipse(g, skin, x, base - 162 * k, 46 * k, 46 * k);
}

/** Person: ein Porträt (Kopf und Schultern) */
function person(g: Ctx, slot: number): void {
  const { top, bottom } = autoAvatarOf(slot);
  background(g, top, bottom);
  figure(g, slot, 128, 1);
}

/** Organisation: drei Figuren, die mittlere vorn */
function organisation(g: Ctx, slot: number): void {
  const top = autoColorOf(slot);
  background(g, top, shade(top, 0.28));
  figure(g, slot + 3, 56, 0.68, 200);
  figure(g, slot + 5, 200, 0.68, 200);
  figure(g, slot, 128, 0.84, 214);
}

/** Ort: ein Haus mit Turm (Satteldach, Turm mit Spitzdach links) auf dem Boden */
function location(g: Ctx, slot: number): void {
  const top = autoColorOf(slot);
  background(g, top, shade(top, 0.2));
  const wall = shade(autoColorOf(slot + 7), -0.25);
  const roof = shade(autoColorOf(slot + 11), 0.5);
  const dark = shade(top, 0.65);
  rect(g, shade(top, 0.42), 0, 170, SIZE, SIZE - 170);
  // auf dem Boden (gezeichnet bis y 222), verkleinert, damit Dachspitze und Boden auch zugeschnitten zu sehen sind
  g.translate(128, 170);
  g.scale(0.75, 0.75);
  g.translate(-128, -222);
  rect(g, wall, 96, 136, 108, 86);
  poly(g, roof, [[90, 140], [150, 100], [210, 140]]);
  rect(g, shade(wall, 0.08), 52, 76, 44, 146);
  poly(g, roof, [[48, 80], [74, 28], [100, 80]]);
  rect(g, dark, 68, 96, 12, 18);
  rect(g, dark, 140, 180, 20, 42);
  g.resetTransform();
}

/** Landschaft: Himmel, Sonne, zwei Hügelketten */
function landscape(g: Ctx, slot: number, rnd: () => number): void {
  const top = autoColorOf(slot);
  background(g, shade(top, -0.25), top);
  ellipse(g, shade(autoColorOf(slot + 13), -0.55), between(rnd, 60, 196), between(rnd, 40, 76), 24, 24);
  const hills = (color: string, base: number, amp: number): void => {
    g.fillStyle = color;
    g.beginPath();
    g.moveTo(0, SIZE);
    g.lineTo(0, base + between(rnd, -amp, amp));
    const peak = between(rnd, 40, 216);
    g.quadraticCurveTo(peak, base - amp * 2.2, SIZE, base + between(rnd, -amp, amp));
    g.lineTo(SIZE, SIZE);
    g.closePath();
    g.fill();
  };
  const hill = autoColorOf(slot + 7);
  hills(shade(hill, 0.25), 128, 22);
  hills(shade(hill, 0.5), 166, 16);
}

/** Gegenstand: ein Buch (Deckel, Rücken, Seiten) mit Schatten */
function objects(g: Ctx, slot: number): void {
  const top = autoColorOf(slot);
  background(g, top, shade(top, 0.28));
  const body = shade(autoColorOf(slot + 7), 0.35);
  const edge = shade(body, 0.45);
  g.translate(0, -34);
  ellipse(g, shade(top, 0.4), 128, 214, 78, 12);
  rect(g, shade(top, -0.6), 82, 74, 108, 140);
  rect(g, body, 70, 66, 108, 140);
  rect(g, edge, 70, 66, 18, 140);
  rect(g, edge, 108, 104, 52, 8);
  g.resetTransform();
}

/** Konzept: drei sich überlagernde Grundformen */
function concept(g: Ctx, slot: number, rnd: () => number): void {
  const top = autoColorOf(slot);
  background(g, top, shade(top, 0.28));
  g.globalAlpha = 0.85;
  for (let i = 0; i < 3; i++) {
    const color = shade(autoColorOf(slot + 5 + i * 6), 0.2 + i * 0.15);
    const r = between(rnd, 34, 54);
    const x = between(rnd, 70, 186);
    const y = between(rnd, 70, 130);
    const kind = Math.floor(rnd() * 3);
    if (kind === 0) ellipse(g, color, x, y, r, r);
    else if (kind === 1) rect(g, color, x - r, y - r, 2 * r, 2 * r);
    else poly(g, color, [[x, y - r * 1.15], [x + r * 1.15, y + r * 0.85], [x - r * 1.15, y + r * 0.85]]);
  }
  g.globalAlpha = 1;
}

/** Szene: ein Blatt mit Eselsohr und Textzeilen, in der Farbe ihres Kapitels (`color`; ohne Kapitel neutral) */
function scene(g: Ctx, _slot: number, _rnd: () => number, color: string): void {
  const top = color;
  background(g, top, shade(top, 0.28));
  const [x, y, w, h, fold] = [80, 44, 96, 132, 26];
  poly(g, shade(top, -0.6), [[x, y], [x + w - fold, y], [x + w, y + fold], [x + w, y + h], [x, y + h]]);
  poly(g, shade(top, 0.18), [[x + w - fold, y], [x + w - fold, y + fold], [x + w, y + fold]]);
  const line = shade(top, 0.45);
  for (let i = 0; i < 6; i++) rect(g, line, x + 16, y + 40 + i * 14, i === 5 ? 36 : w - 32, 5);
  rect(g, line, x + 16, y + 18, 34, 7);
}

const DRAW: Record<AutoTemplate, (g: Ctx, slot: number, rnd: () => number, color: string) => void> = {
  scene,
  person,
  organisation,
  location,
  landscape,
  objects,
  concept,
};

/** Bild eines Elements oder einer Szene ohne eigenes Bild (core `parseAutoImageId`; Farbplatz null: neutral) als PNG-Data-URI */
export function renderAvatar({ template, slot, color }: { template: AutoTemplate; slot: number | null; color: string }): string {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const g = canvas.getContext('2d')!;
  DRAW[template](g, slot ?? 0, randomOf(slot ?? 0, Object.keys(DRAW).indexOf(template)), color);
  return canvas.toDataURL('image/png');
}
